import { useState } from "react";
import { Episode } from "@/types/podcast";
import dayjs from "dayjs";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HighlightText } from "@/components/common/HighlightText";
import { useSearchParams } from "react-router-dom";

interface MatchedEpisodesProps {
  episodes?: Episode[];
}

export function MatchedEpisodes({ episodes }: MatchedEpisodesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  
  // Get keywords from URL
  const keywords = searchParams.get("episodeKeywords") 
    ? searchParams.get("episodeKeywords")?.split(',').filter(Boolean) 
    : [];

  if (!episodes || episodes.length === 0) {
    return null;
  }

  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-0 text-xs text-primary hover:text-primary/80 hover:bg-transparent p-0 font-medium flex items-center gap-1"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
        {isExpanded ? "Hide" : "Show"} {episodes.length} matched episode{episodes.length !== 1 ? 's' : ''}
      </Button>

      {isExpanded && (
        <div className="mt-2 pl-2 border-l-2 border-primary/20 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
          {episodes.map((episode) => (
            <div key={episode.id} className="text-sm">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-700 line-clamp-1" title={episode.title}>
                        <HighlightText text={episode.title} keywords={keywords} maxLength={60} />
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                        {dayjs(episode.publishedAt).format('MMM D, YYYY')}
                    </span>
                </div>
                {episode.description && (
                     <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        <HighlightText text={episode.description} keywords={keywords} maxLength={120} />
                     </p>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
